import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, BellRing, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { authFetch } from '../lib/auth';
import { TEAM_AVATARS } from './teamAvatars';

/* Rachel Smart Push — proactive, native-style notifications with quick actions. */

const SEV = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
  info: 'bg-sky-500',
};

const seenSet = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem('kx_rachel_push_seen') || '[]')); } catch { return new Set(); }
};
const markSeen = (pid) => {
  const s = seenSet();
  s.add(pid);
  try { sessionStorage.setItem('kx_rachel_push_seen', JSON.stringify([...s])); } catch { /* noop */ }
};

export default function RachelPush({ isEs }) {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const notified = useRef(new Set());
  const rachel = TEAM_AVATARS.find((a) => a.key === 'rachel');

  const load = async () => {
    try {
      const r = await authFetch('/admin/ai-workspace/push');
      if (!r.ok) return;
      const j = await r.json();
      const seen = seenSet();
      setQueue((j.pushes || []).filter((p) => !seen.has(p.pid)));
    } catch { /* noop */ }
  };

  useEffect(() => {
    const t = setTimeout(load, 2500);
    const iv = setInterval(load, 240000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = queue[0];

  useEffect(() => {
    if (!cur || notified.current.has(cur.pid)) return;
    notified.current.add(cur.pid);
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // eslint-disable-next-line no-new
        new Notification(`Rachel · ${isEs ? cur.title_es : cur.title_en}`,
          { body: isEs ? cur.body_es : cur.body_en, icon: '/kitxens-logo.png' });
      }
    } catch { /* noop */ }
  }, [cur, isEs]);

  const dismiss = (pid) => {
    markSeen(pid);
    authFetch('/admin/ai-workspace/push/dismiss', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pid }) }).catch(() => {});
    setQueue((q) => q.filter((p) => p.pid !== pid));
  };

  const act = async (p) => {
    const a = p.action || {};
    if (a.type === 'navigate') {
      dismiss(p.pid);
      navigate(a.to);
      return;
    }
    if (a.type === 'assign') {
      setBusy(true);
      try {
        const r = await authFetch('/admin/ai-workspace/tasks', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agent: a.agent, title: isEs ? a.task_es : a.task_en }) });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const name = a.agent.charAt(0).toUpperCase() + a.agent.slice(1);
        toast.success(isEs ? `Tarea asignada a ${name} ✓ · Eva notificada` : `Task assigned to ${name} ✓ · Eva notified`);
        dismiss(p.pid);
      } catch (e) {
        toast.error(String(e.message));
      } finally {
        setBusy(false);
      }
    }
  };

  const reply = (p) => {
    dismiss(p.pid);
    navigate('/admin/ai-os/autonomy?tab=team&agent=rachel');
  };

  const enableBrowserPush = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') toast.success(isEs ? 'Notificaciones del navegador activadas' : 'Browser notifications enabled');
    } catch { /* noop */ }
  };

  const canAskPerm = typeof Notification !== 'undefined' && Notification.permission === 'default';

  return (
    <AnimatePresence>
      {cur && (
        <motion.div
          key={cur.pid}
          initial={{ opacity: 0, y: -24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -16, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          className="fixed z-[95] top-3 inset-x-3 sm:inset-x-auto sm:right-4 sm:top-4 sm:w-[390px]"
          role="alert"
          data-testid="rachel-push-card">
          <div className="rounded-2xl bg-white border border-gray-200 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] overflow-hidden">
            <span className={`block h-1 ${SEV[cur.severity] || SEV.info}`} aria-hidden="true" />
            <div className="p-4">
              <div className="flex items-start gap-3">
                <span className="relative shrink-0">
                  {rachel ? (
                    <img src={rachel.img} alt="Rachel" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <span className="flex w-10 h-10 rounded-full bg-[#F77F11] text-white items-center justify-center text-sm font-bold">R</span>
                  )}
                  <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white flex items-center justify-center text-[10px]" aria-hidden="true">{cur.icon}</span>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#F77F11]">Rachel</p>
                    <span className="text-[10px] text-gray-300">·</span>
                    <p className="text-[10.5px] text-gray-400">{isEs ? 'ahora' : 'now'}</p>
                    {queue.length > 1 && (
                      <span className="ml-auto text-[10.5px] font-semibold text-gray-400" data-testid="rachel-push-more">+{queue.length - 1}</span>
                    )}
                  </div>
                  <p className="mt-0.5 text-[13.5px] font-bold text-gray-900 leading-snug" data-testid="rachel-push-title">
                    {isEs ? cur.title_es : cur.title_en}
                  </p>
                  <p className="mt-1 text-[12.5px] text-gray-500 leading-relaxed">{isEs ? cur.body_es : cur.body_en}</p>
                </div>
                <button onClick={() => dismiss(cur.pid)} aria-label={isEs ? 'Descartar' : 'Dismiss'}
                  className="shrink-0 p-1 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                  data-testid="rachel-push-dismiss">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2 pl-[52px]">
                {cur.action && (
                  <button onClick={() => act(cur)} disabled={busy}
                    className="h-8 px-3.5 rounded-full bg-gray-900 text-white text-[12px] font-bold hover:opacity-90 active:scale-95 transition disabled:opacity-50"
                    data-testid="rachel-push-primary-action">
                    {busy ? '…' : (isEs ? cur.action.label_es : cur.action.label_en)}
                  </button>
                )}
                <button onClick={() => reply(cur)}
                  className="h-8 px-3 rounded-full border border-gray-200 text-gray-600 text-[12px] font-semibold hover:border-gray-300 inline-flex items-center gap-1.5 transition-colors"
                  data-testid="rachel-push-reply">
                  <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" /> {isEs ? 'Responder' : 'Reply'}
                </button>
                {canAskPerm && (
                  <button onClick={enableBrowserPush} title={isEs ? 'Activar push del navegador' : 'Enable browser push'}
                    className="ml-auto p-1.5 rounded-lg text-gray-300 hover:text-[#F77F11] transition-colors"
                    data-testid="rachel-push-enable-native">
                    <BellRing className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
